import React, { useState } from 'react';
import { ArrowLeft, Paperclip, Download, Send, User, Calendar, FileText, FileSpreadsheet, File, CheckCheck, Trash2, Archive } from 'lucide-react';
import { API_BASE, apiFetch } from '../config';
import { useLanguage } from '../i18n';

const EmailDetail = ({ email, onBack, onReplyFile }) => {
  const { t } = useLanguage();
  const [isRead, setIsRead] = useState(email?.is_read || false);
  const [marking, setMarking] = useState(false);
  const [deleting, setDeleting] = useState(false);

  if (!email) return null;

  const handleMarkRead = async () => {
    setMarking(true);
    try {
      const res = await apiFetch(API_BASE + '/api/emails/' + email.id + '/read', { method: 'PATCH' });
      if (res.ok) {
        setIsRead(true);
        email.is_read = true;
      }
    } catch (err) {
      console.error('Mark read error:', err);
    } finally {
      setMarking(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('email_delete_confirm'))) return;
    setDeleting(true);
    try {
      const res = await apiFetch(API_BASE + '/api/emails/' + email.id, { method: 'DELETE' });
      if (res.ok) onBack();
    } catch (err) {
      console.error('Delete error:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleArchive = async () => {
    setDeleting(true);
    try {
      const res = await apiFetch(API_BASE + '/api/emails/' + email.id + '/archive', { method: 'PATCH' });
      if (res.ok) onBack();
    } catch (err) {
      console.error('Archive error:', err);
    } finally {
      setDeleting(false);
    }
  };

  const handleDownloadAttachment = async (idx, filename) => {
    try {
      const res = await apiFetch(`${API_BASE}/api/emails/${email.id}/attachments/${idx}`);
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'file';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const getFileIcon = (type) => {
    switch (type) {
      case 'pdf': return <FileText size={18} className="text-red-400" />;
      case 'spreadsheet': return <FileSpreadsheet size={18} className="text-emerald-400" />;
      default: return <File size={18} className="text-blue-400" />;
    }
  };

  return (
    <div className="animate-slide-in-right pb-24">
      <div className="flex items-center gap-3 mb-5">
        <button
          id="back-btn"
          onClick={onBack}
          className="w-10 h-10 glass-card flex items-center justify-center rounded-xl
                     hover:bg-white/10 transition-colors active:scale-95"
        >
          <ArrowLeft size={18} className="text-white/70" />
        </button>
        <h2 className="text-lg font-bold text-white/90 truncate flex-1">{t('email_detail_title')}</h2>
      </div>

      <div className="glass-card p-5 mb-4 relative overflow-hidden">
        <div className="absolute -top-6 -right-6 w-20 h-20 bg-blue-500/10 rounded-full blur-2xl" />

        <h3 className="text-base font-bold text-white/90 mb-4 relative z-10">{email.subject}</h3>

        <div className="space-y-3 relative z-10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <User size={14} className="text-blue-400" />
            </div>
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider">{t('email_from')}</p>
              <p className="text-sm text-white/80 font-mono">{email.sender}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-cyan-500/10 flex items-center justify-center">
              <Calendar size={14} className="text-cyan-400" />
            </div>
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider">{t('email_date')}</p>
              <p className="text-sm text-white/80">{email.date}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="glass-card p-5 mb-4">
        <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider mb-3">{t('email_body_title')}</h4>
        <div className="text-sm text-white/70 leading-relaxed whitespace-pre-line">
          {email.body || t('email_body_empty')}
        </div>
      </div>

      {email.attachments && email.attachments.length > 0 && (
        <div className="glass-card p-5 mb-4">
          <div className="flex items-center gap-2 mb-4">
            <Paperclip size={14} className="text-white/40" />
            <h4 className="text-xs font-semibold text-white/40 uppercase tracking-wider">
              {t('email_attachments')} ({email.attachments.length})
            </h4>
          </div>

          <div className="space-y-2">
            {email.attachments.map((att, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between bg-white/5 rounded-xl p-3 
                           hover:bg-white/10 transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center">
                    {getFileIcon(att.type)}
                  </div>
                  <div>
                    <p className="text-sm text-white/80 font-medium truncate max-w-[180px]">{att.name}</p>
                    <p className="text-[10px] text-white/30">{att.size}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleDownloadAttachment(idx, att.name)}
                  className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center
                                   hover:bg-blue-500/20 transition-colors active:scale-95"
                >
                  <Download size={14} className="text-blue-400" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3">
        {!isRead && (
          <button
            onClick={handleMarkRead}
            disabled={marking}
            className="flex-1 glass-card py-3 flex items-center justify-center gap-2 
                       text-emerald-400 hover:bg-white/5 transition-colors active:scale-[0.98] disabled:opacity-50"
          >
            {marking ? (
              <div className="w-4 h-4 border-2 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin" />
            ) : (
              <CheckCheck size={16} />
            )}
            <span className="text-sm font-medium">{t('email_mark_read')}</span>
          </button>
        )}
        <button
          onClick={() => onReplyFile && onReplyFile(email)}
          className="flex-1 btn-primary flex items-center justify-center gap-3"
        >
          <Send size={16} />
          <span>{t('email_reply_file')}</span>
        </button>
      </div>

      {/* Delete / Archive */}
      <div className="flex gap-3 mt-3">
        <button
          onClick={handleArchive}
          disabled={deleting}
          className="flex-1 glass-card py-2.5 flex items-center justify-center gap-2 text-amber-400 hover:bg-white/5 transition-colors active:scale-[0.98] disabled:opacity-50"
        >
          <Archive size={14} />
          <span className="text-xs font-medium">{t('email_archive')}</span>
        </button>
        <button
          onClick={handleDelete}
          disabled={deleting}
          className="flex-1 glass-card py-2.5 flex items-center justify-center gap-2 text-red-400 hover:bg-red-500/5 transition-colors active:scale-[0.98] disabled:opacity-50"
        >
          <Trash2 size={14} />
          <span className="text-xs font-medium">{t('email_delete')}</span>
        </button>
      </div>
    </div>
  );
};

export default EmailDetail;
